import { useCallback, useEffect, useRef } from "react";
import { sharedMemoryCache } from "src-next/shared/cache/createMemoryCache";
import { useCachedResource } from "src-next/shared/cache/useCachedResource";
import {
  fetchOrganizationPersons,
  organizationPersonsCacheKey,
} from "src-next/modules/organization-patients";

const EMPTY_PERSONS: fhir5.Person[] = [];

/**
 * Persons da organização do CRM (extrato FluxZap).
 * Falha isolada: não derruba Patients nem o histórico.
 *
 * Usa a mesma chave de cache do `PatientOrganizationContext`
 * (`org-patients:persons:{org}`): extrato, dialogs e contexto dividem
 * um único pedido. Fechar e abrir não busca de novo.
 */
export function useCommunicationsOrganizationPersons(
  organizationId?: string,
  revision = 0,
) {
  const trimmed = organizationId?.trim() || "";
  const cacheKey = organizationPersonsCacheKey(trimmed);

  const { data, error, refresh } = useCachedResource<fhir5.Person[]>({
    cacheKey,
    fetcher: () => fetchOrganizationPersons(trimmed),
  });

  /**
   * `revision` mudou (ex.: envio concluído): força a rede.
   * Sem dado ainda, o `useCachedResource` já está buscando — não duplica.
   */
  const revisionRef = useRef(revision);
  useEffect(() => {
    if (revisionRef.current === revision) return;
    revisionRef.current = revision;
    if (!cacheKey) return;
    if (!sharedMemoryCache.getKeySnapshot(cacheKey).entry) return;
    void refresh().catch(() => undefined);
  }, [cacheKey, refresh, revision]);

  const refetch = useCallback(() => {
    if (cacheKey) void refresh().catch(() => undefined);
  }, [cacheKey, refresh]);

  return {
    persons: data ?? EMPTY_PERSONS,
    refetch,
    hasFetchedOnce: data !== undefined || Boolean(error),
  };
}
